import * as Sentry from '@sentry/react'
import { companyApi, clientesBack } from '@/common/api/https'

/**
 * Obtiene los datos del contrato (empresa, CUIT, etc.)
 * @param {number|string} numeroContrato - Número de contrato
 * @returns {Promise<{code: number, data: object|null, headers?: object}>}
 */
export const getDatosContrato = async (numeroContrato) => {
  try {
    const res = await companyApi.get(`/v2/datos/${numeroContrato}`)
    return {
      code: res.status,
      data: res.data || null,
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'COMPANY')
        scope.setTag('endpoint', `/v2/datos/${numeroContrato}`)
        scope.setExtras({ numeroContrato, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al obtener datos del contrato',
    }
  }
}

/**
 * Obtiene los datos del ejecutivo asignado al contrato
 * @param {number|string} numeroContrato - Número de contrato
 * @returns {Promise<{code: number, data: object|null, headers?: object}>}
 */
export const getEjecutivoContrato = async (numeroContrato) => {
  try {
    const res = await companyApi.get(`/ejecutivodeservicio/${numeroContrato}`)
    return {
      code: res.status,
      data: res.data || null,
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'COMPANY')
        scope.setTag('endpoint', `/ejecutivodeservicio/${numeroContrato}`)
        scope.setExtras({ numeroContrato, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al obtener datos del ejecutivo',
    }
  }
}

/**
 * Cambia el contrato asociado a la sesión activa.
 *
 * El backend valida que el usuario tenga permiso sobre ese contrato antes de
 * actualizar la sesión. Responde 200 sin devolver el contrato nuevo, así que
 * quien llame tiene que releer la sesión (checkAuth) para propagar el cambio.
 *
 * @param {Object} params
 * @param {string} params.uuid - Token del usuario (userToken), UUID v4
 * @param {number} params.idUsuario - ID del usuario
 * @param {number} params.contrato - Contrato al que se cambia
 * @param {string} params.cuit - CUIT de la cuenta, 11 dígitos sin separadores
 * @returns {Promise<{code: number, data: object|null, message?: string}>}
 */
export const cambiarContratoSesion = async ({ uuid, idUsuario, contrato, cuit }) => {
  try {
    const res = await clientesBack.post('/v1/login/contrato', {
      uuid,
      idUsuario,
      contrato,
      cuit,
    })
    return {
      code: res.status,
      data: res.data || null,
      message: 'Contrato de sesión actualizado correctamente',
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v1/login/contrato')
        scope.setExtras({ idUsuario, contrato, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al cambiar de cuenta',
    }
  }
}

/**
 * Cierra la sesión del usuario revocando el token en Keycloak
 * @param {string} uuid - Token del usuario (userToken)
 * @param {string} usuarioBaja - Email del usuario que cierra sesión
 * @returns {Promise<{code: number, data: object|null, headers?: object}>}
 */
export const logoutUsuario = async (uuid, usuarioBaja) => {
  try {
    const res = await clientesBack.post('/v1/logout', {
      uuid,
      usuarioBaja,
    })
    return {
      code: res.status,
      data: res.data || null,
      headers: res.headers,
    }
  } catch (e) {
    const status = e?.response?.status ?? 0
    const isServerSide = status === 0 || status >= 500
    if (isServerSide) {
      Sentry.withScope((scope) => {
        scope.setTag('service', 'CLIENTES_BACK')
        scope.setTag('endpoint', '/v1/logout')
        scope.setExtras({ uuid: uuid?.substring(0, 10), usuarioBaja, method: e?.config?.method })
        Sentry.captureException(e)
      })
    }
    return {
      code: status || 500,
      data: null,
      message: e?.response?.data?.message || 'Error al cerrar sesión',
    }
  }
}
